/**
 * DocumentApprovalDialog — used by the Universal Approval Requests bell
 * (EditAccessRequest.jsx). When an approver clicks a Documents-Corner
 * request the FULL native document detail is shown with in-modal action
 * buttons that match the module's own workflow:
 *   • pending_admin  — admin approvers see Approve / Reject
 *   • pending_master — Master Admin sees Approve / Reject
 *   • pending_review — assigned reviewer sees Good-to-Go / Send-back-for-Revision
 * Every action calls the same endpoints the Documents Corner module uses,
 * so the mirrored `edit_access_requests` row stays in sync automatically.
 */
import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { fmtDateTimeIST } from "../lib/datetime";
import {
  FileText, ShieldAlert, Download, ExternalLink, CheckCircle2, XCircle,
  RefreshCcw, Users, Clock, Globe2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  draft:           { label: "Draft",           cls: "bg-slate-100 text-slate-700 border-slate-300" },
  pending_admin:   { label: "Pending Admin",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
  pending_review:  { label: "Pending Review",  cls: "bg-violet-100 text-violet-800 border-violet-300" },
  pending_master:  { label: "Pending Master",  cls: "bg-orange-100 text-orange-800 border-orange-300" },
  scheduled:       { label: "Scheduled",       cls: "bg-sky-100 text-sky-800 border-sky-300" },
  approved:        { label: "Approved",        cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected:        { label: "Rejected",        cls: "bg-rose-100 text-rose-800 border-rose-300" },
  expired:         { label: "Expired",         cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

const BASE_ADMIN_ROLES = new Set([
  "hr_admin", "sales_admin", "accounts_admin",
  "dispatch_admin", "it_admin", "crm_manager",
]);

function statusPill(s) {
  const m = STATUS_LABEL[s] || { label: s, cls: "bg-slate-100 text-slate-700 border-slate-300" };
  return <Badge className={`${m.cls} border`}>{m.label}</Badge>;
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function DocumentApprovalDialog({
  open,
  onClose,
  documentId,
  currentUser,   // { id, role }
  onActionDone,
}) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reviseMode, setReviseMode] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !documentId) return;
    let cancelled = false;
    setLoading(true);
    setDoc(null);
    setRejectMode(false);
    setReviseMode(false);
    setRemarks("");
    (async () => {
      try {
        const { data } = await api.get(`/documents/${documentId}`);
        if (!cancelled) setDoc(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatError(e.response?.data?.detail) || "Failed to load document");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, documentId, onClose]);

  const uid = currentUser?.id;
  const role = currentUser?.role;
  const isMaster = role === "master_admin";
  const isAdminApprover = isMaster || BASE_ADMIN_ROLES.has(role);
  const isCreator = doc && (doc.created_by_id === uid || doc.uploaded_by_id === uid);
  const status = doc?.status;

  const canAdminApprove = doc && !isCreator && status === "pending_admin" && isAdminApprover;
  const canMasterApprove = doc && !isCreator && status === "pending_master" && isMaster;
  const canApprove = canAdminApprove || canMasterApprove;
  const canReject = canApprove;

  const iAmReviewer = doc && (doc.reviewers || []).some((r) => r.user_id === uid);
  const iAlreadyApproved = doc && (doc.reviewers || []).some((r) => r.user_id === uid && r.good_to_go);
  const canReviewerAct = doc && status === "pending_review" && iAmReviewer;

  const refreshDoc = async () => {
    try {
      const { data } = await api.get(`/documents/${doc.id}`);
      setDoc(data);
    } catch { /* ignore */ }
  };

  const doApprove = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/approve`);
      toast.success("Approved");
      onActionDone?.("approved");
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Approve failed");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    const r = remarks.trim();
    if (r.length < 3) { toast.error("Rejection remarks (min 3 chars) required"); return; }
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/reject`, { remarks: r });
      toast.success("Rejected");
      onActionDone?.("rejected");
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Reject failed");
    } finally { setBusy(false); }
  };

  const doGoodToGo = async () => {
    if (!window.confirm("Mark this document as Good-to-Go? It will move forward for Master Admin approval once every reviewer approves.")) return;
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/reviewers/good-to-go`);
      toast.success("Marked Good-to-Go");
      onActionDone?.("good_to_go");
      await refreshDoc();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const doRevise = async () => {
    const r = remarks.trim();
    if (r.length < 3) { toast.error("Please write a short note for the editor (min 3 chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/reviewers/revise`, { remarks: r });
      toast.success("Sent back to editor for revision");
      onActionDone?.("revise");
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="doc-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {doc?.is_important
              ? <ShieldAlert className="w-5 h-5 text-rose-600" />
              : <FileText className="w-5 h-5 text-indigo-600" />}
            {doc?.title || (loading ? "Loading…" : "Document")}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-slate-400 text-sm">Loading document…</div>
        )}

        {!loading && doc && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {statusPill(doc.status)}
              {doc.is_important && (
                <Badge className="bg-rose-100 text-rose-800 border-rose-200">
                  Important
                </Badge>
              )}
              {doc.tag && doc.tag !== "other" && (
                <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200 capitalize">
                  {doc.tag.replace(/_/g, " ")}
                </Badge>
              )}
              {doc.tag === "other" && doc.tag_custom && (
                <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200">
                  {doc.tag_custom}
                </Badge>
              )}
              {doc.revise_pending && (
                <Badge className="bg-amber-500 text-white border-amber-600 animate-pulse">
                  <RefreshCcw className="w-3 h-3 mr-1" /> Revise Requested
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <div><span className="text-slate-400">Created by:</span> <strong>{doc.created_by_name}</strong></div>
              <div><span className="text-slate-400">Created at:</span> {fmtDateTimeIST(doc.created_at)}</div>
              {doc.published_at && (
                <div><span className="text-slate-400">Published:</span> {fmtDateTimeIST(doc.published_at)}</div>
              )}
              {doc.end_at && (
                <div><span className="text-slate-400">Expires:</span> {fmtDateTimeIST(doc.end_at)}</div>
              )}
              {doc.scheduled_for && doc.status === "scheduled" && (
                <div className="col-span-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sky-900 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Scheduled for: {fmtDateTimeIST(doc.scheduled_for)}
                </div>
              )}
              {doc.is_important && (
                <div className="col-span-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                  Important documents require <strong>Master Admin approval</strong> — no auto-approve.
                </div>
              )}
              {doc.status === "rejected" && doc.rejection_remarks && (
                <div className="col-span-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                  <div className="font-medium mb-0.5">Rejected by {doc.rejected_by_name}</div>
                  <div className="text-[12px] opacity-80">Remarks: {doc.rejection_remarks}</div>
                </div>
              )}
            </div>

            {doc.description && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1">Description</div>
                <div className="whitespace-pre-wrap text-slate-800 text-[13px] leading-relaxed rounded-md border border-slate-200 bg-slate-50/60 p-3">
                  {doc.description}
                </div>
              </div>
            )}

            {(doc.attachments || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1">
                  Links &amp; Docs · {doc.attachments.length}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="doc-approval-attachments">
                  {doc.attachments.map((a, i) => (
                    <div key={i} className="border border-slate-200 rounded-md bg-white px-3 py-2 flex items-center gap-2" data-testid={`doc-approval-att-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-800 truncate">
                          {a.type === "url" ? (a.hostname || a.external_url) : a.filename}
                        </div>
                        <div className="text-[10.5px] text-slate-500 truncate">
                          {a.type === "url" ? a.external_url : `${fmtSize(a.size)} · ${a.content_type || "file"}`}
                        </div>
                      </div>
                      {a.type === "url" ? (
                        <a href={a.external_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700 text-white">
                            <ExternalLink size={11} className="mr-1" /> Open
                          </Button>
                        </a>
                      ) : (
                        <a
                          href={`${BACKEND}/api/documents/${doc.id}/attachment/${i}/download`}
                          className="inline-flex"
                        >
                          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white">
                            <Download size={11} className="mr-1" /> Download
                          </Button>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1 flex items-center gap-1">
                <Users className="w-3 h-3" /> Audience
              </div>
              {doc.target_all || doc.applicable_to_all ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-bold text-[12.5px]">
                  <Globe2 className="w-3.5 h-3.5" /> All Employees
                </span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {(doc.target_user_names || doc.allowed_user_names || []).map((n, i) => (
                    <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-800">{n}</Badge>
                  ))}
                  {!(doc.target_user_names?.length || doc.allowed_user_names?.length) && (
                    <span className="text-slate-400 text-[12px]">—</span>
                  )}
                </div>
              )}
            </div>

            {(doc.reviewers || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1">
                  Reviewers · {doc.reviewers.length}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {doc.reviewers.map((r, i) => (
                    <Badge
                      key={i}
                      className={r.good_to_go
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-amber-100 text-amber-800 border-amber-300"}
                    >
                      {r.good_to_go ? "✓ " : "⏳ "}{r.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {(doc.approval_history || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1">
                  Approval Trail
                </div>
                <div className="space-y-1">
                  {doc.approval_history.map((h, i) => {
                    const isApprove = (h.action || "").startsWith("approved");
                    const isReject = h.action === "rejected";
                    return (
                      <div key={i} className={`text-[11.5px] rounded-md px-2 py-1.5 border ${
                        isReject ? "bg-rose-50 border-rose-200"
                        : isApprove ? "bg-emerald-50 border-emerald-200"
                        : "bg-slate-50 border-slate-200"}`}>
                        <div className="flex items-center gap-1.5">
                          {isApprove
                            ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            : isReject
                              ? <XCircle className="w-3 h-3 text-rose-600" />
                              : <Clock className="w-3 h-3 text-slate-500" />}
                          <strong className="text-slate-800">{h.name}</strong>
                          <span className="text-slate-500">· {(h.action || "").replace(/_/g, " ")}</span>
                          <span className="text-slate-400 font-mono ml-auto">{fmtDateTimeIST(h.at)}</span>
                        </div>
                        {h.remarks && <div className="text-slate-600 pl-4 mt-0.5">&ldquo;{h.remarks}&rdquo;</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          {canApprove && !rejectMode && !reviseMode && (
            <>
              <Button
                onClick={doApprove}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="doc-approval-approve-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
              </Button>
              <Button
                variant="destructive"
                onClick={() => { setRejectMode(true); setRemarks(""); }}
                disabled={busy}
                data-testid="doc-approval-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1.5" /> Reject
              </Button>
            </>
          )}
          {canReject && rejectMode && (
            <div className="w-full space-y-2">
              <Label>Rejection remarks</Label>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value.slice(0, 500))}
                placeholder="Explain why this document is being rejected"
                data-testid="doc-approval-reject-remarks"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setRejectMode(false); setRemarks(""); }} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={doReject}
                  disabled={busy || remarks.trim().length < 3}
                  data-testid="doc-approval-reject-confirm"
                >
                  Confirm Reject
                </Button>
              </div>
            </div>
          )}

          {canReviewerAct && !reviseMode && (
            <>
              <Button
                onClick={doGoodToGo}
                disabled={busy || iAlreadyApproved}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="doc-approval-g2g-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                {iAlreadyApproved ? "Already Approved" : "Approve — Good to Go"}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setReviseMode(true); setRemarks(""); }}
                disabled={busy}
                className="border-amber-400 text-amber-800 hover:bg-amber-50"
                data-testid="doc-approval-revise-btn"
              >
                <RefreshCcw className="w-4 h-4 mr-1.5" /> Send back for Revision
              </Button>
            </>
          )}
          {canReviewerAct && reviseMode && (
            <div className="w-full space-y-2">
              <Label>Note for the editor</Label>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value.slice(0, 500))}
                placeholder="Summarise the changes needed before this doc can proceed"
                data-testid="doc-approval-revise-remarks"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setReviseMode(false); setRemarks(""); }} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  onClick={doRevise}
                  disabled={busy || remarks.trim().length < 3}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="doc-approval-revise-confirm"
                >
                  Send back
                </Button>
              </div>
            </div>
          )}

          {!canApprove && !canReviewerAct && doc && !loading && (
            <div className="text-[11.5px] text-slate-500 italic mr-auto">
              {status && !["pending_admin", "pending_master", "pending_review"].includes(status)
                ? `Document is already ${status.replace(/_/g, " ")} — no action needed.`
                : "You are not authorised to act on this document at its current stage."}
            </div>
          )}

          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
